<template>
  <div class="bg-white rounded-lg shadow-sm p-4 border border-gray-300">
    <div class="relative mb-2">
      <h3 class="text-sm font-semibold text-gray-800 text-center">
        {{ monthName }}
      </h3>
      <button
        type="button"
        class="absolute right-0 top-1/2 -translate-y-1/2 text-xs text-indigo-800 font-medium whitespace-nowrap px-3 py-1.5 rounded-md bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-900 transition-colors"
        @click="goToBookings"
      >
        Ir para reservas
      </button>
    </div>
    <div class="grid grid-cols-7 gap-1 text-[11px] text-gray-400 mb-1">
      <span v-for="d in weekDays" :key="d" class="text-center">{{ d }}</span>
    </div>
    <div class="grid grid-cols-7 gap-1 text-xs">
      <span
        v-for="n in firstWeekdayOffset"
        :key="'empty-' + n"
      />
      <div
        v-for="day in daysInMonth"
        :key="day"
        class="h-8 w-8 flex items-center justify-center rounded-md text-[11px]"
        :class="dayClasses(day)"
        :title="dayTooltip(day)"
      >
        {{ day }}
      </div>
    </div>
    <div v-if="movements && movements.length" class="mt-3">
      <button
        type="button"
        class="text-xs text-blue-600 hover:text-blue-800 font-medium"
        @click="toggleDetails"
      >
        {{ showDetails ? '- Detalhes' : '+ Detalhes' }}
      </button>
      <div v-if="showDetails" class="mt-2 border-t border-gray-200 pt-2">
        <table class="w-full text-[11px] text-left">
          <thead>
            <tr class="text-gray-500">
              <th class="py-1 pr-2">Tipo</th>
              <th class="py-1 pr-2">Nome</th>
              <th class="py-1 pr-2">Início</th>
              <th class="py-1 pr-2">Fim</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in sortedMovements"
              :key="m.id"
              class="border-t border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td class="py-1 pr-2">
                <span
                  class="text-[11px] font-medium"
                  :class="typeTextClasses(m)"
                >
                  {{ m.kind }}
                </span>
              </td>
              <td class="py-1 pr-2 text-gray-800">
                <span :title="m.customer_name || ''">
                  {{ truncateName(m.customer_name, 15) }}
                </span>
              </td>
              <td class="py-1 pr-2 text-gray-800 whitespace-nowrap">
                {{ formatDate(m.check_in_date) }}
              </td>
              <td class="py-1 pr-2 text-gray-800 whitespace-nowrap">
                {{ formatDate(m.check_out_date) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { navigateTo } from '../router.js'
const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

export default {
  name: 'AvailabilityMonthCalendar',
  props: {
    year: {
      type: Number,
      required: true
    },
    monthIndex: {
      type: Number,
      required: true // 0 = Jan ... 11 = Dez
    },
    unavailableDates: {
      type: Object, // Set de 'YYYY-MM-DD'
      required: true
    },
    dayLabels: {
      type: Object, // { 'YYYY-MM-DD': ['Hospedagem (Daniel)', ...] }
      required: false,
      default: () => ({})
    },
    movements: {
      type: Array, // [{ id, kind, customer_name, check_in_date, check_in_time, check_out_date, check_out_time }]
      required: false,
      default: () => []
    },
    propertyId: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  data() {
    return {
      showDetails: false
    }
  },
  computed: {
    monthName() {
      return MONTH_NAMES[this.monthIndex]
    },
    daysInMonth() {
      return new Date(this.year, this.monthIndex + 1, 0).getDate()
    },
    firstWeekdayOffset() {
      const first = new Date(this.year, this.monthIndex, 1)
      // 0 = Dom, 1 = Seg ... mantemos dom como primeira coluna
      return first.getDay()
    },
    weekDays() {
      return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    },

    /**
     * Ordena os movimentos para exibir detalhes sempre por:
     * 1) check_in_date (data de início)
     * 2) check_out_date (desempate por fim)
     * 3) id (desempate final)
     */
    sortedMovements() {
      return [...(this.movements || [])].sort((a, b) => {
        const ai = a.check_in_date || ''
        const bi = b.check_in_date || ''
        if (ai !== bi) return ai.localeCompare(bi)

        const ao = a.check_out_date || ''
        const bo = b.check_out_date || ''
        if (ao !== bo) return ao.localeCompare(bo)

        return (a.id || 0) - (b.id || 0)
      })
    }
  },
  methods: {
    goToBookings() {
      navigateTo('bookings', {
        year: this.year,
        monthIndex: this.monthIndex,
        propertyId: this.propertyId,
        openNewBooking: true
      })
    },
    truncateName(name, max = 15) {
      const v = name || ''
      if (!v) return '-'
      if (v.length <= max) return v
      return `${v.slice(0, max)}...`
    },
    dayTooltip(day) {
      const date = new Date(this.year, this.monthIndex, day)
      const iso = date.toISOString().slice(0, 10)
      const labels = this.dayLabels[iso]
      return Array.isArray(labels) && labels.length > 0 ? labels.join(' / ') : ''
    },
    dayClasses(day) {
      const date = new Date(this.year, this.monthIndex, day)
      const iso = date.toISOString().slice(0, 10)

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const isPast = date < today

      const labels = this.dayLabels[iso] || []
      const hasService = labels.some((l) => typeof l === 'string' && l.startsWith('Serviço'))
      const hasStay = labels.some((l) => typeof l === 'string' && l.startsWith('Hospedagem'))
      const hasMovement = this.unavailableDates.has(iso) || hasService || hasStay

      if (hasMovement) {
        // Cor por tipo de movimento
        if (hasService && !hasStay) {
          // Serviço: laranja (amber), igual tela de hospedagens
          return 'bg-amber-100 text-amber-800'
        }
        if (hasStay && !hasService) {
          // Hospedagem: roxo/indigo, igual tela de hospedagens
          return 'bg-indigo-100 text-indigo-800'
        }
        // Misturado (hospedagem + serviço no mesmo dia): vermelho
        return 'bg-red-100 text-red-700'
      }

      if (isPast) {
        // Dias passados sem movimento: cinza
        return 'bg-gray-100 text-gray-500'
      }

      // Dias futuros sem movimento: disponível (verde)
      return 'bg-green-100 text-green-700'
    },
    toggleDetails() {
      this.showDetails = !this.showDetails
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const [year, month, day] = dateStr.split('-')
      return `${day}/${month}/${year}`
    },
    typeTextClasses(movement) {
      const kind = (movement.kind || '').toLowerCase()
      if (kind.startsWith('serviço')) {
        return 'text-amber-700'
      }
      if (kind.startsWith('hospedagem')) {
        return 'text-indigo-700'
      }
      return 'text-red-700'
    }
  }
}
</script>

